/**
 * @file commonSlice.js
 * @description Redux slice for managing common application-level navigation states such as route tracking and path information.
 *              Uses Redux Toolkit's createSlice for simplified reducer and action creation.
 *              Includes support for resetting state via a custom action type.
 * @author govind dindewad
 * @date 28 january 2025
 */

import { createSlice } from '@reduxjs/toolkit';

// Initial state for common navigation-related data
const initialState = {
    routeStatus: '/rolemanagement',        // Default route status path
    platformPath: '/',                     // Default platform path
    poolVerificationPath: '/poolverification', // Path for pool verification section
    poolPath: '/pools',                    // Path for pool section
    pathInfo: null                         // Dynamic path info (optional, can store context-specific data)
};

// Create the slice
const commonSlice = createSlice({
    name: 'common',
    initialState,
    reducers: {
        /**
         * @function setRouteStatus
         * @description Updates the current route status.
         */
        setRouteStatus: (state, action) => {
            state.routeStatus = action.payload;
        },

        /**
         * @function setPlatformPath
         * @description Updates the platform path.
         */
        setPlatformPath: (state, action) => {
            state.platformPath = action.payload;
        },

        /**
         * @function setPoolVerificationPath
         * @description Updates the pool verification path.
         */
        setPoolVerificationPath: (state, action) => {
            state.poolVerificationPath = action.payload;
        },

        /**
         * @function setPoolPath
         * @description Updates the pool path.
         */
        setPoolPath: (state, action) => {
            state.poolPath = action.payload;
        },

        /**
         * @function setPathInfo
         * @description Stores arbitrary path-related info.
         */
        setPathInfo: (state, action) => {
            state.pathInfo = action.payload;
        }
    },

    // Extra reducers for handling external actions like a full state reset
    extraReducers: (builder) => {
        builder.addCase('RESET_STATE', () => initialState);
    }
});

// Export actions to be used in components or middleware
export const {
    setRouteStatus,
    setPlatformPath,
    setPoolVerificationPath,
    setPoolPath,
    setPathInfo
} = commonSlice.actions;

// Export the reducer to be included in the store
export default commonSlice.reducer;
